import { useState } from "react";
import { ArrowRight, CheckCircle, AlertCircle } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const Newsletter = () => {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState({ type: null, message: "" });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email || !email.includes("@")) {
      setStatus({ type: "error", message: "Please enter a valid email address" });
      return;
    }

    setLoading(true);
    setStatus({ type: null, message: "" });

    try {
      const response = await axios.post(`${BACKEND_URL}/api/newsletter/subscribe`, {
        email: email,
      });

      setStatus({ type: "success", message: response.data.message });
      setEmail("");
    } catch (error) {
      const errorMessage = error.response?.data?.detail || "Something went wrong. Try again?";
      setStatus({ type: "error", message: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="newsletter"
      className="px-5 sm:px-8 lg:px-12 py-16 md:py-40 border-t border-[#232332]"
      data-testid="newsletter-section"
    >
      <div className="max-w-4xl">
        <p className="overline-tcc mb-6">The newsletter — zero spam, occasional cupcakes</p>
        <h2 className="font-display text-6xl sm:text-8xl lg:text-[9rem] leading-[0.85] text-[#F4F2EE] mb-8 break-words">
          Stay<br />
          <span className="text-stroke-pink">chaotic</span>
          <span className="text-[#C8FF3E]">.</span>
        </h2>
        <p className="text-[#9494A6] font-light text-base md:text-lg max-w-md mb-12">
          New episodes, behind-the-scenes chaos, and things you'll actually care about —
          delivered straight from the messy middle to your inbox.
        </p>

        {/* Form */}
        <form onSubmit={handleSubmit} noValidate className="max-w-xl">
          <div className="flex items-end gap-0 border-b border-white/20 focus-within:border-[#FF3E8A] transition-colors duration-200">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              className="flex-1 bg-transparent border-none outline-none py-5 text-xl sm:text-2xl md:text-3xl font-light text-[#F4F2EE] placeholder:text-[#4A4A58] min-w-0"
              disabled={loading}
              data-testid="newsletter-email-input"
            />
            <button
              type="submit"
              className="shrink-0 w-14 h-14 md:w-16 md:h-16 mb-2 bg-[#C8FF3E] hover:bg-[#FF3E8A] text-[#08080D] flex items-center justify-center transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={loading}
              aria-label="Subscribe"
              data-testid="newsletter-submit-button"
            >
              <ArrowRight size={22} className={loading ? "animate-pulse" : ""} />
            </button>
          </div>

          {status.type && (
            <div
              className={`mt-5 flex items-center gap-2 font-mono-tcc text-xs ${
                status.type === "success" ? "text-[#C8FF3E]" : "text-[#FF3B30]"
              }`}
              data-testid="newsletter-status"
            >
              {status.type === "success" ? <CheckCircle size={14} /> : <AlertCircle size={14} />}
              {status.message}
            </div>
          )}

          <p className="font-mono-tcc text-[10px] uppercase tracking-[0.25em] text-[#5E5E72] mt-6">
            We respect your inbox. Unsubscribe anytime.
          </p>
        </form>
      </div>
    </section>
  );
};
